'use client';

import searchContract from '@bells/api/search/frontend';
import { useAppSettings } from '@bells/core';
import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import {
  crudNotFound,
  layoutSearchAttendants,
  layoutSearchFaq,
  layoutSearchModalDescription,
  layoutSearchModalTitle,
  layoutSearchPages,
  layoutSearchPosts,
  layoutSearchTitle,
} from '@bells/i18n';
import { SearchGroup } from '@bells/schema/models/search';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@bells/ui/components/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@bells/ui/components/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@bells/ui/components/drawer';
import { groupBy } from 'lodash';
import { HelpCircle, Loader2, RssIcon, StickyNote, User } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useMediaQuery } from 'usehooks-ts';

const systemRestClient = generateRestClient(searchContract);

const Result = () => {
  const { setUseSearch } = useAppSettings();
  const [searchInput, setSearchInput] = useState<string>();

  const [debouncedSearchInput] = useDebounce(searchInput, 150);

  const { language } = useLanguage();

  const { data, isLoading } = systemRestClient.doSearch.useQuery({
    queryKey: ['page-search', debouncedSearchInput],
    queryData: {
      query: { term: debouncedSearchInput ?? '', locale: language },
    },
    enabled:
      debouncedSearchInput && debouncedSearchInput.length > 1 ? true : false,
    select: (df) => df.body,
  });

  const grouped = useMemo(() => {
    return groupBy(data, 'group');
  }, [data]);

  const router = useRouter();

  const resolveGroupTitle = (val: string) => {
    if (val === SearchGroup.ATTENDANT) {
      return layoutSearchAttendants();
    } else if (val === SearchGroup.PAGE) {
      return layoutSearchPages();
    } else if (val === SearchGroup.POST) {
      return layoutSearchPosts();
    } else if (val === SearchGroup.FAQ) {
      return layoutSearchFaq();
    }

    return undefined;
  };

  return (
    <Command
      className='h-full w-full flex-1 bg-transparent'
      shouldFilter={false}
    >
      <CommandInput
        value={searchInput}
        className='font-medium'
        onValueChange={(val) => setSearchInput(val)}
        placeholder={layoutSearchTitle()}
      />
      <CommandList className='h-full max-h-max bg-transparent'>
        <CommandEmpty>
          {!data ||
            (data?.length === 0 && (
              <div className='flex items-center justify-center py-4'>
                {crudNotFound()}
              </div>
            ))}
        </CommandEmpty>

        <CommandGroup className={cn('relative h-auto', { hidden: !isLoading })}>
          <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        </CommandGroup>

        {Object.keys(grouped).map((groupKey) => {
          return (
            <CommandGroup key={groupKey} heading={resolveGroupTitle(groupKey)}>
              {(grouped[groupKey] ?? []).map((item, index) => {
                return (
                  <CommandItem
                    value={`${item.link}`}
                    onSelect={(val) => {
                      router.push(val);
                      setUseSearch(false);
                    }}
                    key={`search-${groupKey}-${index}`}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <div className='flex w-full justify-between gap-2'>
                      <div className='flex w-full flex-1 items-center gap-1'>
                        {item.title}
                      </div>

                      {item.group == SearchGroup.ATTENDANT && (
                        <User className='h-4 w-4 text-muted-foreground'></User>
                      )}
                      {item.group == SearchGroup.PAGE && (
                        <StickyNote className='h-4 w-4 text-muted-foreground'></StickyNote>
                      )}
                      {item.group == SearchGroup.POST && (
                        <RssIcon className='h-4 w-4 text-muted-foreground'></RssIcon>
                      )}
                      {item.group == SearchGroup.FAQ && (
                        <HelpCircle className='h-4 w-4 text-muted-foreground'></HelpCircle>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          );
        })}
      </CommandList>
    </Command>
  );
};

export default function PageSearch() {
  const { setUseSearch, useSearch } = useAppSettings();

  const isDesktop = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
        if (
          (e.target instanceof HTMLElement && e.target.isContentEditable) ||
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement
        ) {
          return;
        }

        e.preventDefault();
        setUseSearch(!useSearch);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);
  return isDesktop ? (
    <Dialog open={useSearch} onOpenChange={setUseSearch}>
      <DialogContent className='flex h-auto w-full flex-col sm:max-h-[500px] sm:max-w-[500px] lg:max-h-[700px] lg:max-w-[700px]'>
        <DialogHeader>
          <DialogTitle>{layoutSearchModalTitle()}</DialogTitle>
          <DialogDescription>
            {layoutSearchModalDescription()}
          </DialogDescription>
        </DialogHeader>
        <Result></Result>
      </DialogContent>
    </Dialog>
  ) : (
    <Drawer open={useSearch} onOpenChange={setUseSearch}>
      <DrawerContent className='flex h-[75vh] w-full flex-col'>
        <DrawerHeader>
          <DrawerTitle>{layoutSearchModalTitle()}</DrawerTitle>
          <DrawerDescription>
            {layoutSearchModalDescription()}
          </DrawerDescription>
        </DrawerHeader>
        <Result></Result>
      </DrawerContent>
    </Drawer>
  );
}
